import { useEffect, type RefObject } from 'react'
import { touchUndoRegion } from '@/src/renderer/lib/undo'

/** Alles innerhalb dieser Klasse ist das Brett, alles andere auf der Seite die Liste. */
const BOARD_SELECTOR = '.project-canvas-panel'

/**
 * **Wer `⌘Z` bekommt: die zuletzt berührte Hälfte** (ADR 0028).
 *
 * Auf einer Projektseite mit aufgezogener **Pinnwand** liegen zwei
 * Rücknahme-Wege nebeneinander — die Liste mit ihrer **gemerkten Handlung**
 * und das Brett mit seinem eigenen Stapel. Vorher gewann das Brett, solange es
 * offen war: Wer links eine Aufgabe löschte und `⌘Z` drückte, sah einen Zettel
 * zurückspringen.
 *
 * **Nicht der DOM-Fokus.** Den hat die Pinnwand schon einmal geprüft und
 * wieder abgeschafft — nach einem Löschen fällt er unzuverlässig, oft auf ein
 * umgebendes Element der Projektseite, und ausgerechnet dann käme `⌘Z` nie an
 * (CONTEXT.md, **Rückgängig**). Hier zählt, wo zuletzt geklickt oder getippt
 * WURDE; ein gelöschter Zettel ändert das nicht.
 *
 * **Am `capture`-Lauf**, damit es auch dann greift, wenn ein Griff darunter
 * das Ereignis anhält.
 *
 * Das **Aufziehen** der Pinnwand zählt selbst als Berührung — das setzt
 * `useCanvasHistoryShortcut`, nicht dieser Griff.
 *
 * Die **Aufgaben-Details** zählen zur Liste: Sie gehören der Aufgabe, nicht
 * dem Brett, und was man darin löscht, holt `⌘Z` zurück. Seitenleiste,
 * Titelleiste und Reiterleiste liegen außerhalb der Seite und lassen die
 * Merkung, wie sie ist.
 */
export function useUndoRegionTracking(pageRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const page = pageRef.current
    if (!page) return

    const touch = (e: Event) => {
      const target = e.target as HTMLElement | null
      if (!target?.closest) return
      touchUndoRegion(target.closest(BOARD_SELECTOR) ? 'board' : 'list')
    }

    page.addEventListener('pointerdown', touch, true)
    page.addEventListener('keydown', touch, true)
    return () => {
      page.removeEventListener('pointerdown', touch, true)
      page.removeEventListener('keydown', touch, true)
    }
  }, [pageRef])
}
